import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api, asArray } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PortfolioPage = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");

  useSeo({
    title: "Portfolio Proyek",
    description: "Jejak digital CV. Komunika Teknik — proyek sistem informasi, pengadaan TI, dan infrastruktur untuk pemerintah, BUMN, dan korporasi.",
    path: "/portfolio",
  });

  useEffect(() => {
    api.get("/portfolios", { params: { limit: 100 } })
      .then((r) => setItems(asArray(r.data)))
      .catch(() => setItems([]));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((i) => i.category === filter)),
    [items, filter],
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="portfolio-page">
      <Header />

      <section className="bg-[#0F172A] text-white pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 kt-grid-bg opacity-10" />
        <div className="kt-container relative">
          <div className="kt-eyebrow text-[#FCA5A5]">Portfolio</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
            Jejak digital yang kami bangun bersama klien.
          </h1>
          <p className="mt-6 text-zinc-300 max-w-2xl">
            Kumpulan proyek terpilih lintas sektor — pemerintahan, BUMN, dan
            korporasi swasta — yang merefleksikan keahlian end-to-end kami.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="kt-container">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filter-bar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.16em] font-semibold border transition-colors ${
                  filter === c
                    ? "bg-[#18181B] text-white border-[#18181B]"
                    : "bg-white text-[#18181B] border-[#E4E4E7] hover:border-[#18181B]"
                }`}
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/portfolio/${p.slug}`}
                className="kt-card group overflow-hidden block"
                data-testid={`portfolio-card-${p.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#52525B]">
                    <span>{p.category}</span>
                    <span className="font-mono-kt">{p.year}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-3 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#52525B] mt-1">{p.client}</p>
                  <p className="text-sm text-[#52525B] mt-3 leading-relaxed">
                    {p.summary}
                  </p>
                  {p.tags?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-1 bg-[#F4F4F5] text-[#52525B]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-5 text-xs uppercase tracking-[0.18em] text-[#DC2626] font-semibold inline-flex items-center gap-1.5">
                    Detail Proyek <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-[#52525B]">
              Tidak ada proyek pada kategori ini.
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
